import { alpha } from '@mui/system/colorManipulator';
import { IconAlertCircle, IconCancel } from '@u21/tabler-icons';
import styled, { css, keyframes } from 'styled-components';
import { U21AiChatAttachmentChip } from 'app/shared/u21-ui/components/input/chat/U21AiChatAttachmentChip';
import { U21AiChatToolCallRow } from 'app/shared/u21-ui/components/input/chat/U21AiChatToolCallRow';
import { U21Avatar } from 'app/shared/u21-ui/components/display/avatar/U21Avatar';
import { U21Divider } from 'app/shared/u21-ui/components/display/U21Divider';
import { U21Spacer } from 'app/shared/u21-ui/components/layout/U21Spacer';
import { U21Tooltip } from 'app/shared/u21-ui/components/display/U21Tooltip';
import { U21Typography } from 'app/shared/u21-ui/components/display/typography/U21Typography';
import {
  U21AiChatMessage,
  U21AiChatMessageStatus,
} from 'app/shared/u21-ui/components/input/chat/types';
import { U21Themes } from 'vendor/material-minimal/models';

type UserBubbleVariant = 'cancelled' | 'default' | 'failed' | 'pending';

const getUserBubbleVariant = (
  status: U21AiChatMessageStatus,
): UserBubbleVariant => {
  if (status === 'pending') {
    return 'pending';
  }
  if (status === 'cancelled') {
    return 'cancelled';
  }
  if (status === 'failed') {
    return 'failed';
  }
  return 'default';
};

const CANCELLED_MESSAGE_TEXT = 'Message cancelled by user';

interface Props {
  agentName: string;
  agentPicture?: string;
  hasAgentResponseBelow: boolean;
  message: U21AiChatMessage;
  failedTooltip?: string;
  formattedTimestamp: string;
}

export const U21AiChatMessageBubble = ({
  agentName,
  agentPicture,
  failedTooltip,
  formattedTimestamp,
  hasAgentResponseBelow,
  message,
}: Props) => {
  const isFromAgent = message.sender === 'agent';

  if (isFromAgent) {
    return (
      <TurnSpacer spacing={2}>
        <AgentMessageRow align="end" horizontal>
          <U21Avatar
            name={message.senderName}
            size="small"
            src={message.senderPicture || undefined}
          />
          <MessageBubble $isFromAgent>
            <MessageText breakWord variant="body1">
              {message.text}
            </MessageText>
            <U21Typography color="text.secondary" variant="caption">
              {formattedTimestamp}
            </U21Typography>
          </MessageBubble>
        </AgentMessageRow>
      </TurnSpacer>
    );
  }

  const bubbleVariant = getUserBubbleVariant(message.status);
  const isFailed = message.status === 'failed';
  const isCancelled = message.status === 'cancelled';
  const showToolCallAvatar = !hasAgentResponseBelow;

  return (
    <TurnSpacer spacing={2}>
      <UserMessageRow align="end" horizontal spacing={1}>
        <StatusAndBubbleWrapper horizontal spacing={0.5} align="center">
          {isFailed && failedTooltip && (
            <U21Tooltip placement="top" tooltip={failedTooltip}>
              <StatusIconWrap>
                <FailedStatusIcon
                  aria-label={failedTooltip}
                  role="img"
                  size={16}
                />
              </StatusIconWrap>
            </U21Tooltip>
          )}
          {isCancelled && (
            <U21Tooltip placement="top" tooltip={CANCELLED_MESSAGE_TEXT}>
              <StatusIconWrap>
                <CancelledStatusIcon
                  aria-label={CANCELLED_MESSAGE_TEXT}
                  role="img"
                  size={16}
                />
              </StatusIconWrap>
            </U21Tooltip>
          )}
          <MessageBubble
            $isFromAgent={false}
            $variant={bubbleVariant}
            spacing={1}
          >
            <MessageText breakWord color="inherit" variant="body1">
              {message.text}
            </MessageText>
            {message.attachments && message.attachments.length > 0 && (
              <U21Spacer horizontal wrap>
                {message.attachments.map((attachment) => (
                  <U21AiChatAttachmentChip
                    fileName={attachment.name}
                    key={attachment.id}
                    status={{
                      embedUrl: attachment.url,
                      mimeType: attachment.mimeType,
                      state: 'success',
                    }}
                  />
                ))}
              </U21Spacer>
            )}
            <U21Typography color="inherit" variant="caption">
              {formattedTimestamp}
            </U21Typography>
          </MessageBubble>
        </StatusAndBubbleWrapper>
        <U21Tooltip placement="left" tooltip={message.senderName}>
          <U21Avatar
            name={message.senderName}
            size="small"
            src={message.senderPicture || undefined}
          />
        </U21Tooltip>
      </UserMessageRow>
      {message.toolUses && message.toolUses.length > 0 && (
        <ToolUseSection align="end" horizontal spacing={1}>
          <AgentAvatarColumn>
            {showToolCallAvatar && (
              <U21Tooltip placement="right" tooltip={agentName}>
                <U21Avatar
                  name={agentName}
                  size="small"
                  src={agentPicture || undefined}
                />
              </U21Tooltip>
            )}
          </AgentAvatarColumn>
          <ToolUseList spacing={1}>
            {message.toolUses.map((toolUse) => (
              <U21AiChatToolCallRow key={toolUse.id} toolUse={toolUse} />
            ))}
          </ToolUseList>
        </ToolUseSection>
      )}
      {isCancelled && (
        <CancelledFooter>
          <U21Divider horizontal>
            <U21Typography color="text.secondary" variant="caption">
              {CANCELLED_MESSAGE_TEXT}
            </U21Typography>
          </U21Divider>
        </CancelledFooter>
      )}
    </TurnSpacer>
  );
};

const pendingBackgroundShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const TurnSpacer = styled(U21Spacer)`
  max-width: 100%;
  min-width: 0;
  width: 100%;
`;

const UserMessageRow = styled(U21Spacer)`
  justify-content: flex-end;
  max-width: 100%;
  min-width: 0;
  padding-bottom: ${({ theme }) => theme.spacing(2)};
  width: 100%;
`;

const AgentMessageRow = styled(U21Spacer)`
  max-width: 100%;
  min-width: 0;
  width: 100%;
`;

const StatusIconWrap = styled.span`
  display: inline-flex;
  flex-shrink: 0;
`;

const FailedStatusIcon = styled(IconAlertCircle)`
  color: ${({ theme }) => theme.palette.warning.main};
`;

const CancelledStatusIcon = styled(IconCancel)`
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const MessageBubble = styled(U21Spacer)<{
  $isFromAgent: boolean;
  $variant?: UserBubbleVariant;
}>`
  padding: ${({ theme }) => theme.spacing(1.5, 2)};
  border-radius: ${({ theme }) => theme.shape.borderRadiusSm}px;
  border: 1px solid transparent;

  ${({ $isFromAgent, theme }) =>
    $isFromAgent &&
    css`
      background-color: ${theme.palette.background.neutral};
      color: inherit;
    `}

  ${({ $isFromAgent, $variant, theme }) =>
    !$isFromAgent &&
    $variant === 'default' &&
    css`
      background-color: ${theme.palette.primary.main};
      color: ${theme.palette.primary.contrastText};
    `}

  ${({ $isFromAgent, $variant, theme }) =>
    !$isFromAgent &&
    $variant === 'cancelled' &&
    css`
      background-color: ${theme.palette.grey[500_16]};
      border-color: ${theme.palette.grey[500_24]};
      color: ${theme.palette.text.secondary};
    `}

  ${({ $isFromAgent, $variant, theme }) =>
    !$isFromAgent &&
    $variant === 'failed' &&
    css`
      background-color: ${alpha(theme.palette.warning.main, 0.12)};
      border-color: ${alpha(theme.palette.warning.main, 0.24)};
      color: ${theme.palette.mode === U21Themes.LIGHT
        ? theme.palette.warning.dark
        : theme.palette.warning.light};
    `}

  ${({ $isFromAgent, $variant, theme }) =>
    !$isFromAgent &&
    $variant === 'pending' &&
    css`
      animation: ${pendingBackgroundShift} 3s ease infinite;
      background: linear-gradient(
        270deg,
        ${theme.palette.special.lighter},
        ${theme.palette.special.main},
        ${theme.palette.special.light}
      );
      background-size: 200% 200%;
      color: ${theme.palette.special.contrastText};
    `}
`;

const StatusAndBubbleWrapper = styled(U21Spacer)`
  max-width: 70%;
  min-width: 0;
`;

const ToolUseSection = styled(U21Spacer)`
  max-width: 100%;
  min-width: 0;
  padding-bottom: ${({ theme }) => theme.spacing(1)};
  width: 100%;
`;

const AgentAvatarColumn = styled.div`
  align-items: flex-end;
  display: flex;
  flex-shrink: 0;
  height: ${({ theme }) => theme.spacing(2.5)};
  justify-content: center;
  width: ${({ theme }) => theme.spacing(2.5)};
`;

const ToolUseList = styled(U21Spacer)`
  flex: 1;
  max-width: 100%;
  min-width: 0;
`;

const CancelledFooter = styled.div`
  padding-left: ${({ theme }) => theme.spacing(4)};
  width: 100%;
`;

const MessageText = styled(U21Typography)`
  white-space: pre-wrap;
`;
